import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { IIncidentModalComponent } from '@max/incident/i-incident-modal.component';
import { IncidentLawEnforcementModel } from '@max/incident/models/incident-law-enforcement.model';
import { IncidentTypeModel } from '@max/incident/models/incident-type.model';
import { IncidentService } from '@max/incident/services/incident.service';
import { LawEnforcementIncidentService } from '@max/incident/services/law-enforcement-incident.service';
import { toDateOrNull, toDateStringOrNull, toNumberOrNull, toStringOrNull } from '@max/incident/shared/data-helpers';
import { IncidentDialogContext } from '@max/incident/shared/incident-dialog-context.model';
import { BaseComponent } from '@max/shared/base-components/base.component';
import { MvdModalService } from '@max/shared/services/modal/mvd-modal.service';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

@Component({
    selector: 'law-enforcement-incident-modal',
    templateUrl: './law-enforcement-incident-modal.html'
})
export class LawEnforcementIncidentModalComponent extends BaseComponent implements IIncidentModalComponent<IncidentLawEnforcementModel> {
    public form: UntypedFormGroup;
    public complaintNumberRequired = false;
    public arrestTimeRequired = false;
    public citingAgencyRequired = false;
    public adminPerSeTestTypeRequired = false;
    public citingAgencyTypes: any[] = [];
    public incidentAdminPerSeTestTypes: any[] = [];
    public accidentTypes: any[] = [];
    public jurisdictions: any [] = [];
    public lawEnforcementAgencies: any[] = [];
    public eyeColorTypes: any[] = [];
    public hairColorTypes: any[] = [];
    public driverLicenseClassTypes: any[] = [];
    public busy = false;
    private derivedCdl = false;
    private csrCdlOverride = false;
    private incidentTypeId: number = null;
    private disableCdlValidation = false;

    private operatingHireFlagIncidentTypeIds: number[] = [1083];

    constructor(
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public data: IncidentDialogContext<IncidentLawEnforcementModel>,
        public dialog: MatDialogRef<LawEnforcementIncidentModalComponent>,
        private lawEnforcementIncidentService: LawEnforcementIncidentService,
        private modalService: MvdModalService,
        private incidentService: IncidentService,
        location: PlatformLocation
    ) {
        super();
        location.onPopState(() => dialog.close(null));
    }

    public ngOnInit(): void {
    }

    public initLookups = (lookups: any): Observable<any> => {
        this.jurisdictions = lookups.vehicleJurisdictions || [];
        this.citingAgencyTypes = lookups.citingAgencyTypes || [];
        this.accidentTypes = lookups.accidentTypes || [];
        this.incidentAdminPerSeTestTypes = lookups.incidentAdminPerSeTestTypes || [];
        this.lawEnforcementAgencies = lookups.lawEnforcementAgencies || [];
        this.eyeColorTypes = lookups.eyeColorTypes || [];
        this.hairColorTypes = lookups.hairColorTypes || [];
        this.driverLicenseClassTypes = lookups.driverLicenseClassTypes || [];

        return this.incidentService.getCDLStatusOnDate(this.data.incident)
            .pipe(tap(r => this.evaluateDriverCdl(r)));
    };

    public closeModal = (result?: any): void => {
        this.dialog.close(result);
    };

    public populateModel = (form: UntypedFormGroup, model: IncidentLawEnforcementModel): void => {
        model.complaintNumber = toStringOrNull(form.get('complaintNumber').value);
        model.citingAgencyTypeId = toNumberOrNull(form.get('citingAgencyTypeId').value);
        model.charge1 = toStringOrNull(form.get('charge1').value);
        model.charge2 = toStringOrNull(form.get('charge2').value);
        model.charge3 = toStringOrNull(form.get('charge3').value);
        model.charge4 = toStringOrNull(form.get('charge4').value);
        model.charge5 = toStringOrNull(form.get('charge5').value);
        model.charge6 = toStringOrNull(form.get('charge6').value);
        model.otherChargeDisplay = toStringOrNull(form.get('otherChargeDisplay').value);
        model.arrestTime = toStringOrNull(form.get('arrestTime').value);
        model.arrestDate = toDateOrNull(form.get('occurrenceDate').value);
        model.arrestLocation = toStringOrNull(form.get('arrestLocation').value);
        model.probableCauseIndicator = (true == form.get('probableCauseIndicator').value) ? '1' : '2';
        model.reasonableGroundsIndicator = (true == form.get('reasonableGroundsIndicator').value) ? '1' : '2';
        model.stopReasonFacts = toStringOrNull(form.get('stopReasonFacts').value);
        model.cdlHolderIndicator = true == form.get('isCDLHolder').value;
        model.csrCdlOverride = this.csrCdlOverride;
        model.commercialMotorVehicleIndicator = true == form.get('commercialMotorVehicleIndicator').value;
        model.hazardousMaterialIndicator = true == form.get('hazardousMaterialIndicator').value;
        model.passengerIndicator = true == form.get('passengerIndicator').value;
        model.operatingHireFlag = true == form.get('operatingHireFlag').value;
        model.admonitionsReadFlag = (true == form.get('admonitionsReadIndicator').value) ? '1' : '2';
        model.incidentAdminPerSeTestTypeId = toNumberOrNull(form.get('incidentAdminPerSeTestTypeId').value);
        model.officerInitials = toStringOrNull(form.get('officerInitials').value);
        model.testStatusBAC = toStringOrNull(form.get('testStatusBAC').value);
        model.testResultsBAC1 = toStringOrNull(form.get('testResultsBAC1').value);
        model.testResultsBAC2 = toStringOrNull(form.get('testResultsBAC2').value);
        model.testStatusDrugs = toStringOrNull(form.get('testStatusDrugs').value);
        model.testResultsDrugs = toStringOrNull(form.get('testResultsDrugs').value);
        model.personRefusedNotes = toStringOrNull(form.get('personRefusedNotes').value);
        model.accidentTypeId = toNumberOrNull(form.get('accidentTypeId').value);
        model.driverLicenseNumber = toStringOrNull(form.get('driverLicenseNumber').value);
        model.driverLicenseJurisdictionId = toNumberOrNull(form.get('driverLicenseJurisdictionId').value);
        model.officerName = toStringOrNull(form.get('officerName').value);
        model.officerBadge = toStringOrNull(form.get('officerBadge').value);
        model.officerSignature = toStringOrNull(form.get('officerSignature').value);
        model.officerStation = toStringOrNull(form.get('officerStation').value);
        model.lawEnforcementAgencyId = toNumberOrNull(form.get('lawEnforcementAgencyId').value);
        model.testOperatorName = toStringOrNull(form.get('testOperatorName').value);
        model.testOperatorNumber = toStringOrNull(form.get('testOperatorNumber').value);
        model.officerName2 = toStringOrNull(form.get('officerName2').value);
        model.officerBadge2 = toStringOrNull(form.get('officerBadge2').value);
        model.officerName3 = toStringOrNull(form.get('officerName3').value);
        model.officerBadge3 = toStringOrNull(form.get('officerBadge3').value);
        model.officerName4 = toStringOrNull(form.get('officerName4').value);
        model.officerBadge4 = toStringOrNull(form.get('officerBadge4').value);
        model.suspensionServedIndicator = true == form.get('suspensionServedIndicator').value;
        model.suspensionServedDate = toDateStringOrNull(form.get('suspensionServedDate').value);
        model.suspensionServedTime = toStringOrNull(form.get('suspensionServedTime').value);
        model.tempPermitEyeColorTypeId = toNumberOrNull(form.get('tempPermitEyeColorTypeId').value);
        model.tempPermitHairColorTypeId = toNumberOrNull(form.get('tempPermitHairColorTypeId').value);
        model.tempPermitDriverLicenseClassTypeId = toNumberOrNull(form.get('tempPermitDriverLicenseClassTypeId').value);
        model.tempPermitEndorsements = toStringOrNull(form.get('tempPermitEndorsements').value);
        model.tempPermitRestrictions = toStringOrNull(form.get('tempPermitRestrictions').value);
        model.tempPermitPermitNotIssuedReason = toStringOrNull(form.get('tempPermitPermitNotIssuedReason').value);
        model.consentToTest = (true == form.get('selectConsentToTestIndicator').value) ? '1' : '2';
        model.admonitions1 = (true == form.get('selectAdmonitions1Indicator').value) ? '1' : '2';
        model.consentToTest_Y = (true == form.get('selectConsentToTestYIndicator').value) ? '1' : '2';
        model.arresteesComments1 = toStringOrNull(form.get('arresteesComments1').value);
        model.admonitions2 = (true == form.get('selectAdmonitions2Indicator').value) ? '1' : '2';
        model.consentToTest_N = (true == form.get('selectConsentToTestNIndicator').value) ? '1' : '2';
        model.arresteesComments2 = toStringOrNull(form.get('arresteesComments2').value);
        model.admonitions3 = (true == form.get('selectAdmonitions3Indicator').value) ? '1' : '2';
        model.consentToTest_N_Delayed = (true == form.get('selectConsentToTestNDelayedIndicator').value) ? '1' : '2';
        model.arresteesComments3 = toStringOrNull(form.get('arresteesComments3').value);
        model.arrestComments = toStringOrNull(form.get('arrestComments').value);
        model.probableCauseFacts = toStringOrNull(form.get('probableCauseFacts').value);
        model.reasonableGroundsFacts = toStringOrNull(form.get('reasonableGroundsFacts').value);
        model.bloodTestResults = toStringOrNull(form.get('bloodTestResults').value);
        model.bloodTestResultsBAC = toStringOrNull(form.get('bloodTestResultsBAC').value);
    };

    public formInitialized = (form: UntypedFormGroup): void => {
        this.form = form;
        form.addControl('complaintNumber', new UntypedFormControl(''));
        form.addControl('citingAgencyTypeId', new UntypedFormControl(null));
        form.addControl('charge1', new UntypedFormControl('', Validators.required));
        form.addControl('charge2', new UntypedFormControl(''));
        form.addControl('charge3', new UntypedFormControl(''));
        form.addControl('charge4', new UntypedFormControl(''));
        form.addControl('charge5', new UntypedFormControl(''));
        form.addControl('charge6', new UntypedFormControl(''));
        form.addControl('otherChargeDisplay', new UntypedFormControl(''));
        form.addControl('arrestTime', new UntypedFormControl(''));
        form.addControl('arrestLocation', new UntypedFormControl(''));
        form.addControl('probableCauseIndicator', new UntypedFormControl(false));
        form.addControl('reasonableGroundsIndicator', new UntypedFormControl(false));
        form.addControl('stopReasonFacts', new UntypedFormControl(''));
        form.addControl('isCDLHolder', new UntypedFormControl(false));
        form.addControl('commercialMotorVehicleIndicator', new UntypedFormControl(false));
        form.addControl('hazardousMaterialIndicator', new UntypedFormControl(false));
        form.addControl('passengerIndicator', new UntypedFormControl(false));
        form.addControl('operatingHireFlag', new UntypedFormControl(false));
        form.addControl('admonitionsReadIndicator', new UntypedFormControl(false));
        form.addControl('incidentAdminPerSeTestTypeId', new UntypedFormControl(null));
        form.addControl('officerInitials', new UntypedFormControl(''));
        form.addControl('testStatusBAC', new UntypedFormControl(''));
        form.addControl('testResultsBAC1', new UntypedFormControl(''));
        form.addControl('testResultsBAC2', new UntypedFormControl(''));
        form.addControl('testStatusDrugs', new UntypedFormControl(''));
        form.addControl('testResultsDrugs', new UntypedFormControl(''));
        form.addControl('personRefusedNotes', new UntypedFormControl(''));
        form.addControl('accidentTypeId', new UntypedFormControl(null, Validators.required));
        form.addControl('driverLicenseNumber', new UntypedFormControl(''));
        form.addControl('driverLicenseJurisdictionId', new UntypedFormControl(null));
        form.addControl('officerName', new UntypedFormControl(''));
        form.addControl('officerBadge', new UntypedFormControl(''));
        form.addControl('officerSignature', new UntypedFormControl(''));
        form.addControl('officerStation', new UntypedFormControl(''));
        form.addControl('lawEnforcementAgencyId', new UntypedFormControl(null, Validators.required));
        form.addControl('testOperatorName', new UntypedFormControl(''));
        form.addControl('testOperatorNumber', new UntypedFormControl(''));
        form.addControl('officerName2', new UntypedFormControl(''));
        form.addControl('officerBadge2', new UntypedFormControl(''));
        form.addControl('officerName3', new UntypedFormControl(''));
        form.addControl('officerBadge3', new UntypedFormControl(''));
        form.addControl('officerName4', new UntypedFormControl(''));
        form.addControl('officerBadge4', new UntypedFormControl(''));
        form.addControl('suspensionServedIndicator', new UntypedFormControl(false));
        form.addControl('suspensionServedDate', new UntypedFormControl(null));
        form.addControl('suspensionServedTime', new UntypedFormControl(''));
        form.addControl('tempPermitEyeColorTypeId', new UntypedFormControl(null));
        form.addControl('tempPermitHairColorTypeId', new UntypedFormControl(null));
        form.addControl('tempPermitDriverLicenseClassTypeId', new UntypedFormControl(null));
        form.addControl('tempPermitEndorsements', new UntypedFormControl(''));
        form.addControl('tempPermitRestrictions', new UntypedFormControl(''));
        form.addControl('tempPermitPermitNotIssuedReason', new UntypedFormControl(''));
        form.addControl('selectConsentToTestIndicator', new UntypedFormControl(false));
        form.addControl('selectAdmonitions1Indicator', new UntypedFormControl(false));
        form.addControl('selectConsentToTestYIndicator', new UntypedFormControl(false));
        form.addControl('arresteesComments1', new UntypedFormControl(''));
        form.addControl('selectAdmonitions2Indicator', new UntypedFormControl(false));
        form.addControl('selectConsentToTestNIndicator', new UntypedFormControl(false));
        form.addControl('arresteesComments2', new UntypedFormControl(''));
        form.addControl('selectAdmonitions3Indicator', new UntypedFormControl(false));
        form.addControl('selectConsentToTestNDelayedIndicator', new UntypedFormControl(false));
        form.addControl('arresteesComments3', new UntypedFormControl(''));
        form.addControl('probableCauseFacts', new UntypedFormControl(''));
        form.addControl('reasonableGroundsFacts', new UntypedFormControl(''));
        form.addControl('bloodTestResultsBAC', new UntypedFormControl(''));
        form.addControl('bloodTestResults', new UntypedFormControl(''));
        form.addControl('arrestComments', new UntypedFormControl(''));
        form.get('isCDLHolder').valueChanges.subscribe(v => this.isCdlHolderChanged(v));
        form.get('occurrenceDate').valueChanges.subscribe(_ => this.occurrenceDateChanged());
        form.get('incidentStatusTypeId').valueChanges.subscribe(v => this.statusChanged(v));
        form.setValidators(c => {
            let probableCause = form.get('probableCauseIndicator').value;
            let reasonableGrounds = form.get('reasonableGroundsIndicator').value;
            let incidentStatusTypeId = form.get('incidentStatusTypeId').value;

            if (!probableCause && !reasonableGrounds && incidentStatusTypeId != 4) {
                return { invalid: true };
            }

            return null;
        });

        if (null != this.incidentTypeId) {
            this.updateFormValidationRules(form, this.incidentTypeId);

            if (false == this.operatingHireFlagIncidentTypeIds.some(v => v == this.incidentTypeId)) {
                this.form.get('operatingHireFlag').disable();
            }
        } else {
            this.form.get('operatingHireFlag').enable();
        }
    };

    public updateFormValidationRules = (form: UntypedFormGroup, incidentTypeId: number): void => {
        this.updateValidators(form, incidentTypeId);
    };

    private statusChanged = (incidentStatusTypeId: number): void => {
        this.updateValidators(this.form, this.incidentTypeId);
    };

    private updateValidators = (form: UntypedFormGroup, incidentTypeId: number): void => {
        if (this.form == null) {
            return;
        }

        this.incidentTypeId = incidentTypeId;
        let complaintNumber = form.get('complaintNumber');
        let citingAgencyTypeId = form.get('citingAgencyTypeId');
        let arrestTime = form.get('arrestTime');
        let incidentAdminPerSeTestTypeId = form.get('incidentAdminPerSeTestTypeId');
        let incidentStatusTypeId = form.get('incidentStatusTypeId').value;
        let charge1 = form.get('charge1');
        let lawEnforcementAgency = form.get('lawEnforcementAgencyId');

        complaintNumber.clearValidators();
        citingAgencyTypeId.clearValidators();
        arrestTime.clearValidators();
        incidentAdminPerSeTestTypeId.clearValidators();
        charge1.clearValidators();
        lawEnforcementAgency.clearValidators();

        complaintNumber.setErrors(null);
        arrestTime.setErrors(null);
        incidentAdminPerSeTestTypeId.setErrors(null);
        charge1.setErrors(null);
        lawEnforcementAgency.setErrors(null);

        this.complaintNumberRequired = false;
        this.arrestTimeRequired = false;
        this.citingAgencyRequired = false;
        this.adminPerSeTestTypeRequired = false;

        const improperUseOfLicenseIncidentTypeId = 52;
        const impliedConsentIncidentTypeId = 53;
        const voidIncidentStatusTypeId = 4;

        if (incidentStatusTypeId != voidIncidentStatusTypeId) {
            charge1.setValidators(Validators.required);
            lawEnforcementAgency.setValidators(Validators.required);

            if (improperUseOfLicenseIncidentTypeId != incidentTypeId) {
                complaintNumber.setValidators(Validators.required);
                this.complaintNumberRequired = true;
                arrestTime.setValidators(Validators.required);
                this.arrestTimeRequired = true;

                if (impliedConsentIncidentTypeId != incidentTypeId) {
                    incidentAdminPerSeTestTypeId.setValidators(Validators.required);
                    this.adminPerSeTestTypeRequired = true;
                }
            }

            if (improperUseOfLicenseIncidentTypeId == incidentTypeId) {
                citingAgencyTypeId.setValidators(Validators.required);
                this.citingAgencyRequired = true;
            }
        }
    };

    public updateData = (form: UntypedFormGroup, incident: IncidentLawEnforcementModel): void => {
        form.get('complaintNumber').setValue(toStringOrNull(incident.complaintNumber));
        form.get('citingAgencyTypeId').setValue(toNumberOrNull(incident.citingAgencyTypeId));
        form.get('charge1').setValue(toStringOrNull(incident.charge1));
        form.get('charge2').setValue(toStringOrNull(incident.charge2));
        form.get('charge3').setValue(toStringOrNull(incident.charge3));
        form.get('charge4').setValue(toStringOrNull(incident.charge4));
        form.get('charge5').setValue(toStringOrNull(incident.charge5));
        form.get('charge6').setValue(toStringOrNull(incident.charge6));
        form.get('otherChargeDisplay').setValue(toStringOrNull(incident.otherChargeDisplay));
        form.get('arrestTime').setValue(toStringOrNull(incident.arrestTime));
        form.get('arrestLocation').setValue(toStringOrNull(incident.arrestLocation));
        form.get('probableCauseIndicator').setValue(1 == toNumberOrNull(incident.probableCauseIndicator));
        form.get('reasonableGroundsIndicator').setValue(1 == toNumberOrNull(incident.reasonableGroundsIndicator));
        form.get('stopReasonFacts').setValue(toStringOrNull(incident.stopReasonFacts));
        form.get('commercialMotorVehicleIndicator').setValue(true == incident.commercialMotorVehicleIndicator);
        form.get('hazardousMaterialIndicator').setValue(true == incident.hazardousMaterialIndicator);
        form.get('passengerIndicator').setValue(true == incident.passengerIndicator);
        form.get('operatingHireFlag').setValue(true == incident.operatingHireFlag);
        form.get('admonitionsReadIndicator').setValue(1 == toNumberOrNull(incident.admonitionsReadFlag));
        form.get('incidentAdminPerSeTestTypeId').setValue(toNumberOrNull(incident.incidentAdminPerSeTestTypeId));
        form.get('officerInitials').setValue(toStringOrNull(incident.officerInitials));
        form.get('testStatusBAC').setValue(toStringOrNull(incident.testStatusBAC));
        form.get('testResultsBAC1').setValue(toStringOrNull(incident.testResultsBAC1));
        form.get('testResultsBAC2').setValue(toStringOrNull(incident.testResultsBAC2));
        form.get('testStatusDrugs').setValue(toStringOrNull(incident.testStatusDrugs));
        form.get('testResultsDrugs').setValue(toStringOrNull(incident.testResultsDrugs));
        form.get('personRefusedNotes').setValue(toStringOrNull(incident.personRefusedNotes));
        form.get('accidentTypeId').setValue(toNumberOrNull(incident.accidentTypeId));
        form.get('driverLicenseNumber').setValue(toStringOrNull(incident.driverLicenseNumber));
        form.get('driverLicenseJurisdictionId').setValue(toNumberOrNull(incident.driverLicenseJurisdictionId));
        form.get('officerName').setValue(toStringOrNull(incident.officerName));
        form.get('officerBadge').setValue(toStringOrNull(incident.officerBadge));
        form.get('officerSignature').setValue(toStringOrNull(incident.officerSignature));
        form.get('officerStation').setValue(toStringOrNull(incident.officerStation));
        form.get('lawEnforcementAgencyId').setValue(toNumberOrNull(incident.lawEnforcementAgencyId));
        form.get('testOperatorName').setValue(toStringOrNull(incident.testOperatorName));
        form.get('testOperatorNumber').setValue(toStringOrNull(incident.testOperatorNumber));
        form.get('officerName2').setValue(toStringOrNull(incident.officerName2));
        form.get('officerBadge2').setValue(toStringOrNull(incident.officerBadge2));
        form.get('officerName3').setValue(toStringOrNull(incident.officerName3));
        form.get('officerBadge3').setValue(toStringOrNull(incident.officerBadge3));
        form.get('officerName4').setValue(toStringOrNull(incident.officerName4));
        form.get('officerBadge4').setValue(toStringOrNull(incident.officerBadge4));
        form.get('suspensionServedIndicator').setValue(true == incident.suspensionServedIndicator);
        form.get('suspensionServedDate').setValue(toDateOrNull(incident.suspensionServedDate));
        form.get('suspensionServedTime').setValue(toStringOrNull(incident.suspensionServedTime));
        form.get('tempPermitEyeColorTypeId').setValue(toNumberOrNull(incident.tempPermitEyeColorTypeId));
        form.get('tempPermitHairColorTypeId').setValue(toNumberOrNull(incident.tempPermitHairColorTypeId));
        form.get('tempPermitDriverLicenseClassTypeId').setValue(toNumberOrNull(incident.tempPermitDriverLicenseClassTypeId));
        form.get('tempPermitEndorsements').setValue(toStringOrNull(incident.tempPermitEndorsements));
        form.get('tempPermitRestrictions').setValue(toStringOrNull(incident.tempPermitRestrictions));
        form.get('tempPermitPermitNotIssuedReason').setValue(toStringOrNull(incident.tempPermitPermitNotIssuedReason));
        form.get('selectConsentToTestIndicator').setValue(1 == toNumberOrNull(incident.consentToTest));
        form.get('selectAdmonitions1Indicator').setValue(1 == toNumberOrNull(incident.admonitions1));
        form.get('selectConsentToTestYIndicator').setValue(1 == toNumberOrNull(incident.consentToTest_Y));
        form.get('arresteesComments1').setValue(toStringOrNull(incident.arresteesComments1));
        form.get('selectAdmonitions2Indicator').setValue(1 == toNumberOrNull(incident.admonitions2));
        form.get('selectConsentToTestNIndicator').setValue(1 == toNumberOrNull(incident.consentToTest_N));
        form.get('arresteesComments2').setValue(toStringOrNull(incident.arresteesComments2));
        form.get('selectAdmonitions3Indicator').setValue(1 == toNumberOrNull(incident.admonitions3));
        form.get('selectConsentToTestNDelayedIndicator').setValue(1 == toNumberOrNull(incident.consentToTest_N_Delayed));
        form.get('arresteesComments3').setValue(toStringOrNull(incident.arresteesComments3));
        form.get('arrestComments').setValue(toStringOrNull(incident.arrestComments));
        form.get('probableCauseFacts').setValue(toStringOrNull(incident.probableCauseFacts));
        form.get('reasonableGroundsFacts').setValue(toStringOrNull(incident.reasonableGroundsFacts));
        form.get('bloodTestResults').setValue(toStringOrNull(incident.bloodTestResults));
        form.get('bloodTestResultsBAC').setValue(toStringOrNull(incident.bloodTestResultsBAC));

        if (null != toNumberOrNull(incident.incidentNumberId)) {
            this.setCdlHolderValue(true == incident.cdlHolderIndicator);
            form.get('otherChargeDisplay').disable();
            form.get('probableCauseIndicator').disable();
            form.get('reasonableGroundsIndicator').disable();
            form.get('stopReasonFacts').disable();
            form.get('hazardousMaterialIndicator').disable();
            form.get('passengerIndicator').disable();
            form.get('testStatusDrugs').disable();
            form.get('testResultsDrugs').disable();
            form.get('suspensionServedIndicator').disable();
            form.get('suspensionServedDate').disable();
            form.get('suspensionServedTime').disable();
            form.get('tempPermitEyeColorTypeId').disable();
            form.get('tempPermitHairColorTypeId').disable();
            form.get('tempPermitEndorsements').disable();
            form.get('tempPermitRestrictions').disable();
        }
    };

    public getIncident = (incidentNumberId: number): Observable<IncidentLawEnforcementModel> => this.lawEnforcementIncidentService.getIncident(incidentNumberId);

    public save = (model: IncidentLawEnforcementModel): Observable<any> => this.lawEnforcementIncidentService.saveIncident(model);

    public loadComplete = (): void => {
    };

    private evaluateDriverCdl = (checkCdl: any): void => {
        this.derivedCdl = true == checkCdl.result;
        this.setCdlHolderValue(this.derivedCdl);
    };

    private setCdlHolderValue = (value: boolean): void => {
        this.disableCdlValidation = true;

        try {
            this.form.get('isCDLHolder').setValue(value);
        } finally {
            this.disableCdlValidation = false;
        }
    };

    private isCdlHolderChanged = (value: boolean): void => {
        if (true == this.disableCdlValidation) {
            return;
        }

        this.csrCdlOverride = false;

        if ((null != this.derivedCdl) && (value != this.derivedCdl)) {
            this.modalService.confirm()
                .title('CDL Holder Confirmation')
                .body('MAX has derived a different value for CDL Holder indicator than what you have selected, do you want to continue?')
                .okBtn('Yes')
                .cancelBtn('No')
                .open()
                .afterClosed()
                .subscribe(r => {
                    if (false == r) {
                        this.form.get('isCDLHolder').setValue(true == this.derivedCdl);
                    } else {
                        this.csrCdlOverride = true;
                    }
                });
        }
    };

    private occurrenceDateChanged = (): void => {
        if (true == this.busy) {
            return;
        }

        if (null == toNumberOrNull(this.data.incident.incidentNumberId) && null != toDateOrNull(this.data.incident.occurrenceDate)) {
            this.busy = true;
            this.incidentService.getCDLStatusOnDate(this.data.incident)
                .pipe(finalize(() => this.busy = false))
                .subscribe(r => {
                    this.setCdlHolderValue(true == r.result);
                    this.derivedCdl = true == r.result;
                },
                e => this.handleValidationResult(e));
        }
    };
}
